// Page Builder Component with Grid System
// Visual editor for building custom portal pages with drag-and-drop content elements
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Register custom font sizes
const Size = Quill.import('attributors/style/size');
Size.whitelist = ['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '36px', '48px', '72px'];
Quill.register(Size, true);
import {
  ArrowLeft,
  Plus,
  Save,
  Eye,
  Settings,
  Trash2,
  Pencil,
  GripVertical,
  Type,
  Image as ImageIcon,
  Calendar,
  BookOpen,
  FolderOpen,
  FileText,
  Grid,
  Move,
  Maximize2,
  Minimize2,
  X,
  Search,
  Settings2,
  Upload,
  CheckCircle
} from 'lucide-react';
import pagesService from '../../services/pagesService';
import api from '../../services/api';
import projectsService from '../../services/projectsService';
import TextBlock from '../blocks/TextBlock';
import ImageBlock from '../blocks/ImageBlock';
import CalendarBlock from '../blocks/CalendarBlock';
import ProjectsBlock from '../blocks/ProjectsBlock';
import DocumentsBlock from '../blocks/DocumentsBlock';
import TutorialsBlock from '../blocks/TutorialsBlock';
import HeroBlock from '../blocks/HeroBlock';
import NewsBlock from '../blocks/NewsBlock';
import ClassScheduleBlock from '../blocks/ClassScheduleBlock';
const BLOCK_TYPES = [
  { type: 'text', label: 'Text Element', icon: Type, description: 'Rich text content' },
  { type: 'image', label: 'Image', icon: ImageIcon, description: 'Image with caption' },
  { type: 'hero', label: 'Hero Section', icon: Maximize2, description: 'Large banner with text overlay' },
  { type: 'news', label: 'News Section', icon: FileText, description: 'News stories with photos' },
  { type: 'class-schedule', label: 'Class Schedule', icon: Calendar, description: 'Monthly class calendar' },
  { type: 'calendar', label: 'Calendar', icon: Calendar, description: 'Class schedule calendar' },
  { type: 'projects', label: 'Projects', icon: BookOpen, description: 'Show projects' },
  { type: 'documents', label: 'Documents', icon: FolderOpen, description: 'Document library' },
  { type: 'tutorials', label: 'Tutorials', icon: FileText, description: 'Project tutorials' }
];

const GRID_COLS = 48;
const CELL_HEIGHT = 20; // pixels per row

// Font options for text elements
const FONTS = [
  { value: 'system', label: 'System Default' },
  { value: 'Coustard', label: 'Coustard' },
  { value: 'Alfa Slab One', label: 'Alfa Slab One' },
  { value: 'Ovo', label: 'Ovo' },
  { value: 'Fraunces', label: 'Fraunces' },
  { value: 'Lancelot', label: 'Lancelot' },
  { value: 'Arvo', label: 'Arvo' },
  { value: 'Josefin Slab', label: 'Josefin Slab' },
  { value: 'Outfit', label: 'Outfit' },
  { value: 'Cal Sans', label: 'Cal Sans' },
  { value: 'Tilt Warp', label: 'Tilt Warp' },
  { value: 'McLaren', label: 'McLaren' },
  { value: 'Mate', label: 'Mate' },
  { value: 'Buda', label: 'Buda' },
  { value: 'Bellefair', label: 'Bellefair' },
  { value: 'Questrial', label: 'Questrial' },
  { value: 'DM Mono', label: 'DM Mono' },
  { value: 'Quicksand', label: 'Quicksand' },
  { value: 'Nunito Sans', label: 'Nunito Sans' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Manrope', label: 'Manrope' },
  { value: 'Jost', label: 'Jost' }
];

const getFontFamily = (fontName) => {
  if (!fontName || fontName === 'system') {
    return 'ui-sans-serif, system-ui, sans-serif';
  }
  return `"${fontName}", sans-serif`;
};

const PageBuilder = ({ page, onClose }) => {
  const [pageData, setPageData] = useState(page);
  const [blocks, setBlocks] = useState(page.draft_content_blocks || page.content_blocks || []);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showPageSettings, setShowPageSettings] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [showAddBlockModal, setShowAddBlockModal] = useState(false);
  const [newBlockType, setNewBlockType] = useState(null);
  const [newBlockContent, setNewBlockContent] = useState({});
  const [newBlockSettings, setNewBlockSettings] = useState({
    padding: 'medium',
    backgroundColor: '',
    gridWidth: 48,
    gridHeight: 4
  });
  const [uploading, setUploading] = useState(false);
  const [portalSettings, setPortalSettings] = useState(null);
  const [navPages, setNavPages] = useState([]);
  const [uploadingBg, setUploadingBg] = useState(false);

  // Projects state for Add Element modal
  const [availableProjects, setAvailableProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectFilterCategory, setProjectFilterCategory] = useState('');
  const [projectFilterDifficulty, setProjectFilterDifficulty] = useState('');
  const [projectFilterTags, setProjectFilterTags] = useState([]);
  const [projectCategories, setProjectCategories] = useState([]);
  const [projectAllTags, setProjectAllTags] = useState([]);

  // Drag state
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [dragMode, setDragMode] = useState(null); // 'move' or 'resize'
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ col: 0, row: 0 });

  const gridRef = useRef(null);

  // Fetch portal settings and pages for header/footer
  useEffect(() => {
    const fetchPortalSettings = async () => {
      try {
        const data = await api.request('/portal/settings');
        setPortalSettings(data.settings);
      } catch (error) {
        console.error('Error fetching portal settings:', error);
      }
    };
    const fetchNavPages = async () => {
      try {
        const response = await pagesService.getPages();
        const pagesData = response.pages || response || [];
        setNavPages(pagesData.filter(p => p.show_in_navigation) || []);
      } catch (error) {
        console.error('Error fetching pages for navigation:', error);
      }
    };
    fetchPortalSettings();
    fetchNavPages();
  }, []);

  useEffect(() => {
    setBlocks(page.draft_content_blocks || page.content_blocks || []);
  }, [page]);

  // Warn user about unsaved changes when leaving page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Check for global background if current page has no background
  useEffect(() => {
    const loadGlobalBackground = async () => {
      console.log('Checking for global background...', {
        pageId: pageData.id,
        hasBackgroundImage: !!pageData.background_image,
        hasBackgroundColor: !!pageData.background_color,
        backgroundType: pageData.background_type
      });

      // Only check if current page doesn't have a background set
      if ((!pageData.background_image && !pageData.background_color) || pageData.background_type === 'none') {
        try {
          const response = await pagesService.getPages();
          const pages = response.pages || [];
          console.log('All pages:', pages.map(p => ({ id: p.id, title: p.title, apply_bg: p.apply_background_to_all })));

          // Find a page with apply_background_to_all enabled
          const globalBgPage = pages.find(p => p.apply_background_to_all);
          console.log('Global bg page found:', globalBgPage);

          if (globalBgPage && globalBgPage.id !== pageData.id) {
            // Fetch the full page data to get background settings
            const fullPage = await pagesService.getPage(globalBgPage.id);
            console.log('Full page data:', {
              background_type: fullPage.background_type,
              background_image: fullPage.background_image,
              background_color: fullPage.background_color
            });

            if (fullPage.background_image || fullPage.background_color) {
              console.log('Applying global background to current page');
              setPageData(prev => ({
                ...prev,
                background_type: fullPage.background_type,
                background_color: fullPage.background_color,
                background_image: fullPage.background_image,
                background_position: fullPage.background_position,
                background_size: fullPage.background_size,
                background_opacity: fullPage.background_opacity,
                _inherited_background: true // Flag to know this is inherited
              }));
            }
          }
        } catch (error) {
          console.error('Error loading global background:', error);
        }
      }
    };

    loadGlobalBackground();
  }, [page.id, pageData.background_type]);

  // Load projects when projects block type is selected
  useEffect(() => {
    if (newBlockType === 'projects') {
      loadProjectsForModal();
    }
  }, [newBlockType]);

  const loadProjectsForModal = async () => {
    try {
      setLoadingProjects(true);
      const response = await projectsService.getProjects();
      const projectsList = response.projects || [];
      setAvailableProjects(projectsList);

      // Extract unique categories and tags
      const uniqueCategories = [...new Set(projectsList.map(p => p.category).filter(Boolean))];
      setProjectCategories(uniqueCategories);

      const uniqueTags = [...new Set(projectsList.flatMap(p => p.tags || []))];
      setProjectAllTags(uniqueTags);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const getFilteredProjects = () => {
    return availableProjects.filter(project => {
      // Search filter
      if (projectSearchTerm) {
        const search = projectSearchTerm.toLowerCase();
        const matchesName = project.name?.toLowerCase().includes(search);
        const matchesDescription = project.description?.toLowerCase().includes(search);
        if (!matchesName && !matchesDescription) return false;
      }

      // Category filter
      if (projectFilterCategory && project.category !== projectFilterCategory) return false;

      // Difficulty filter
      if (projectFilterDifficulty && project.difficulty !== projectFilterDifficulty) return false;

      // Tags filter
      if (projectFilterTags.length > 0) {
        const projectTags = project.tags || [];
        const hasAllTags = projectFilterTags.every(tag => projectTags.includes(tag));
        if (!hasAllTags) return false;
      }

      return true;
    });
  };

  const handleProjectToggle = (projectId) => {
    const selectedProjects = newBlockContent.selectedProjects || [];
    const existing = selectedProjects.find(p => p.projectId === projectId);

    if (existing) {
      setNewBlockContent({
        ...newBlockContent,
        selectedProjects: selectedProjects.filter(p => p.projectId !== projectId)
      });
    } else {
      const project = availableProjects.find(p => p.id === projectId);
      setNewBlockContent({
        ...newBlockContent,
        selectedProjects: [...selectedProjects, {
          projectId: project.id,
          projectName: project.name,
          projectDescription: project.description,
          projectPhoto: project.main_image_url,
          projectHeadline: ''
        }]
      });
    }
  };

  const handleProjectHeadlineChange = (projectId, headline) => {
    const selectedProjects = newBlockContent.selectedProjects || [];
    setNewBlockContent({
      ...newBlockContent,
      selectedProjects: selectedProjects.map(p =>
        p.projectId === projectId ? { ...p, projectHeadline: headline } : p
      )
    });
  };

  const clearProjectFilters = () => {
    setProjectSearchTerm('');
    setProjectFilterCategory('');
    setProjectFilterDifficulty('');
    setProjectFilterTags([]);
  };

  // Calculate grid rows needed (excluding hero blocks)
  const getGridRows = () => {
    const gridBlocks = blocks.filter(b => b.block_type !== 'hero');
    if (gridBlocks.length === 0) return 12;
    const maxRow = Math.max(...gridBlocks.map(b => (b.grid_row || 1) + (b.grid_height || 1) - 1));
    return Math.max(12, maxRow + 4);
  };

  const handleSelectBlockType = (blockType) => {
    setNewBlockType(blockType);
    setNewBlockContent(getDefaultBlockContent(blockType));

    // Set default dimensions per block type
    const defaultDimensions = {
      'calendar': { width: 6, height: 4 },
      'class-schedule': { width: 3, height: 6 },
      'text': { width: 4, height: 4 },
      'image': { width: 6, height: 6 },
      'hero': { width: 12, height: 4 },
      'news': { width: 6, height: 6 },
      'projects': { width: 6, height: 6 },
      'documents': { width: 4, height: 4 },
      'tutorials': { width: 4, height: 4 }
    };

    const dims = defaultDimensions[blockType] || { width: 6, height: 4 };

    setNewBlockSettings({
      padding: 'medium',
      backgroundColor: '',
      gridWidth: dims.width,
      gridHeight: dims.height
    });
    setShowBlockMenu(false);
  };

  const handleAddBlockToPage = async () => {
    try {
      setError(null);

      // Check if trying to add a hero block when one already exists
      if (newBlockType === 'hero') {
        const existingHero = blocks.find(b => b.block_type === 'hero');
        if (existingHero) {
          setError('Only one hero section is allowed per page');
          return;
        }
      }

      // Find next available row (excluding hero blocks)
      const gridBlocks = blocks.filter(b => b.block_type === 'hero');
      const maxRow = gridBlocks.length > 0
        ? Math.max(...gridBlocks.map(b => (b.grid_row || 1) + (b.grid_height || 1) - 1))
        : 0;

      // Hero blocks always go to row 0, full width
      const isHero = newBlockType === 'hero';
      const gridRow = isHero ? 0 : maxRow + 1;

      // NO LONGER CREATE IN DATABASE IMMEDIATELY - only add to local state
      // Create a temporary ID for the new block (will get real ID when published)
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const newBlock = {
        id: tempId,
        block_type: newBlockType,
        block_order: blocks.length,
        content: newBlockContent,
        is_visible: true,
        padding: isHero ? 'none' : newBlockSettings.padding,
        background_color: newBlockSettings.backgroundColor || null,
        grid_column: 1,
        grid_row: gridRow,
        grid_width: isHero ? 48 : newBlockSettings.gridWidth,
        grid_height: isHero ? 10 : newBlockSettings.gridHeight
      };

      // const response = await pagesService.createBlock(pageData.id, newBlock);
      // if (response.block) {
      //   setBlocks([...blocks, response.block]);
      // }

      setBlocks([...blocks, newBlock]);
      setHasUnsavedChanges(true);

      // Reset and close modal
      setShowAddBlockModal(false);
      setNewBlockType(null);
      setNewBlockContent({});
    } catch (error) {
      console.error('Error adding block:', error);
      setError('Failed to add block');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.request('/upload/single', {
        method: 'POST',
        body: formData
      });

      const imageUrl = response.file?.url || response.url;
      setNewBlockContent({ ...newBlockContent, url: imageUrl });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateBlock = async (blockId, updates) => {
    try {
      setError(null);
      // NO LONGER SAVE TO DATABASE IMMEDIATELY - only update local state
      // Changes will be saved to draft when user clicks "Save Draft"
      // await pagesService.updateBlock(pageData.id, blockId, updates);

      // Update local state - convert camelCase to snake_case for all fields
      const localUpdates = { ...updates };
      if (updates.gridColumn !== undefined) {
        localUpdates.grid_column = updates.gridColumn;
        delete localUpdates.gridColumn;
      }
      if (updates.gridRow !== undefined) {
        localUpdates.grid_row = updates.gridRow;
        delete localUpdates.gridRow;
      }
      if (updates.gridWidth !== undefined) {
        localUpdates.grid_width = updates.gridWidth;
        delete localUpdates.gridWidth;
      }
      if (updates.gridHeight !== undefined) {
        localUpdates.grid_height = updates.gridHeight;
        delete localUpdates.gridHeight;
      }
      if (updates.backgroundColor !== undefined) {
        localUpdates.background_color = updates.backgroundColor;
        delete localUpdates.backgroundColor;
      }

      console.log('handleUpdateBlock - updates:', updates);
      console.log('handleUpdateBlock - localUpdates:', localUpdates);

      setBlocks(prevBlocks => {
        const newBlocks = prevBlocks.map(block =>
          block.id === blockId ? { ...block, ...localUpdates } : block
        );
        console.log('handleUpdateBlock - updated block:', newBlocks.find(b => b.id === blockId));
        return newBlocks;
      });
      setHasUnsavedChanges(true);

      // Don't show "saved" message - changes are only in memory until "Save Draft" is clicked
    } catch (error) {
      console.error('Error updating block:', error);
      setError('Failed to update block');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!confirm('Are you sure you want to delete this block?')) {
      return;
    }

    try {
      setError(null);
      // NO LONGER DELETE FROM DATABASE IMMEDIATELY - only update local state
      // Changes will be saved to draft when user clicks "Save Draft"
      // await pagesService.deleteBlock(pageData.id, blockId);
      setBlocks(blocks.filter(block => block.id !== blockId));
      setHasUnsavedChanges(true);
      setSelectedBlock(null);
    } catch (error) {
      console.error('Error deleting block:', error);
      setError('Failed to delete block');
    }
  };

  const handleUpdatePageSettings = async (updates) => {
    try {
      setError(null);
      setSaving(true);
      await pagesService.updatePage(pageData.id, updates);
      setPageData({ ...pageData, ...updates });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error updating page:', error);
      setError('Failed to update page settings');
    } finally {
      setSaving(false);
    }
  };

  const handleBgImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingBg(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.request('/upload/single', {
        method: 'POST',
        body: formData
      });

      const imageUrl = response.file?.url || response.url;
      setPageData({ ...pageData, background_image: imageUrl });
    } catch (error) {
      console.error('Error uploading background image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingBg(false);
    }
  };

  // Save blocks as draft (doesn't publish)
  const handleSaveDraft = async () => {
    try {
      setError(null);
      setSaving(true);
      await pagesService.saveDraft(pageData.id, blocks);
      setSaved(true);
      setHasUnsavedChanges(false);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error saving draft:', error);
      setError('Failed to save draft: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Publish draft to live site
  const handlePublish = async () => {
    try {
      setError(null);
      setPublishing(true);

      // First save the current blocks as draft
      await pagesService.saveDraft(pageData.id, blocks);

      // Then publish the draft
      await pagesService.publishPage(pageData.id);

      setShowPublishConfirm(false);
      setHasUnsavedChanges(false);

      // Refresh page data to update is_published status
      const updatedPage = await pagesService.getPage(pageData.id);
      setPageData(updatedPage);

      // Show success modal with option to view live page
      setShowPublishSuccess(true);
    } catch (error) {
      console.error('Error publishing page:', error);
      setError('Failed to publish page: ' + error.message);
    } finally {
      setPublishing(false);
    }
  };

  const getDefaultBlockContent = (blockType) => {
    switch (blockType) {
      case 'text':
        return { html: '<p>Enter your text here...</p>', alignment: 'left' };
      case 'image':
        return { url: '', alt: '', caption: '', size: 'medium', alignment: 'center' };
      case 'hero':
        return {
          backgroundImage: '',
          overlayText: '',
          textPlacement: 'middle',
          textVertical: 'center',
          textBgEnabled: false,
          textBgColor: '#000000'
        };
      case 'news':
        return {
          stories: []
        };
      case 'class-schedule':
        return {
          months: 3
        };
      case 'background':
        return {
          url: '',
          position: 'fit'
        };
      case 'calendar':
        return { showMonths: 3, highlightEvents: true };
      case 'projects':
        return { featured: false, categoryFilter: '', limit: 6 };
      case 'documents':
        return { category: '', allowDownload: true };
      case 'tutorials':
        return { projectId: '', showSteps: true };
      default:
        return {};
    }
  };

  // Drag and drop handlers
  const handleMouseDown = (e, block, mode) => {
    e.preventDefault();
    e.stopPropagation();

    setDraggedBlock(block);
    setDragMode(mode);
    setDragStart({ x: e.clientX, y: e.clientY });
    setDragOffset({ col: 0, row: 0 });
    setSelectedBlock(block.id);
  };

  const handleMouseMove = (e) => {
    if (!draggedBlock || !gridRef.current) return;

    const gridRect = gridRef.current.getBoundingClientRect();
    const cellWidth = gridRect.width / GRID_COLS;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    const colDelta = Math.round(deltaX / cellWidth);
    const rowDelta = Math.round(deltaY / CELL_HEIGHT);

    if (colDelta !== dragOffset.col || rowDelta !== dragOffset.row) {
      setDragOffset({ col: colDelta, row: rowDelta });
    }
  };

  const handleMouseUp = async () => {
    if (!draggedBlock) return;

    const block = draggedBlock;
    const currentCol = block.grid_column || 1;
    const currentRow = block.grid_row || 1;
    const currentWidth = block.grid_width || 12;
    const currentHeight = block.grid_height || 1;

    let updates = {};

    if (dragMode === 'move') {
      const newCol = Math.max(1, Math.min(GRID_COLS - currentWidth + 1, currentCol + dragOffset.col));
      const newRow = Math.max(1, currentRow + dragOffset.row);

      if (newCol !== currentCol || newRow !== currentRow) {
        updates = {
          gridColumn: newCol,
          gridRow: newRow
        };
      }
    } else if (dragMode === 'resize') {
      const newWidth = Math.max(1, Math.min(GRID_COLS - currentCol + 1, currentWidth + dragOffset.col));
      const newHeight = Math.max(1, currentHeight + dragOffset.row);

      if (newWidth !== currentWidth || newHeight !== currentHeight) {
        updates = {
          gridWidth: newWidth,
          gridHeight: newHeight
        };
      }
    } else if (dragMode === 'resize-vertical') {
      // Only resize height for hero blocks
      const newHeight = Math.max(1, currentHeight + dragOffset.row);

      if (newHeight !== currentHeight) {
        updates = {
          gridHeight: newHeight
        };
      }
    } else if (dragMode === 'resize-nw') {
      // Top-left corner: adjust column, row, width, and height
      const newCol = Math.max(1, currentCol + dragOffset.col);
      const newRow = Math.max(1, currentRow + dragOffset.row);
      const newWidth = Math.max(1, currentWidth - dragOffset.col);
      const newHeight = Math.max(1, currentHeight - dragOffset.row);

      if (newCol !== currentCol || newRow !== currentRow || newWidth !== currentWidth || newHeight !== currentHeight) {
        updates = {
          gridColumn: newCol,
          gridRow: newRow,
          gridWidth: newWidth,
          gridHeight: newHeight
        };
      }
    } else if (dragMode === 'resize-ne') {
      // Top-right corner: adjust row, width, and height
      const newRow = Math.max(1, currentRow + dragOffset.row);
      const newWidth = Math.max(1, Math.min(GRID_COLS - currentCol + 1, currentWidth + dragOffset.col));
      const newHeight = Math.max(1, currentHeight - dragOffset.row);

      if (newRow !== currentRow || newWidth !== currentWidth || newHeight !== currentHeight) {
        updates = {
          gridRow: newRow,
          gridWidth: newWidth,
          gridHeight: newHeight
        };
      }
    } else if (dragMode === 'resize-sw') {
      // Bottom-left corner: adjust column, width, and height
      const newCol = Math.max(1, currentCol + dragOffset.col);
      const newWidth = Math.max(1, currentWidth - dragOffset.col);
      const newHeight = Math.max(1, currentHeight + dragOffset.row);

      if (newCol !== currentCol || newWidth !== currentWidth || newHeight !== currentHeight) {
        updates = {
          gridColumn: newCol,
          gridWidth: newWidth,
          gridHeight: newHeight
        };
      }
    } else if (dragMode === 'resize-left') {
      // Left edge: adjust column and width (for hero blocks)
      const minWidth = 6; // Half page width
      const newCol = Math.max(1, currentCol + dragOffset.col);
      const newWidth = Math.max(minWidth, currentWidth - dragOffset.col);

      // Make sure we don't go below minimum width
      const actualNewCol = currentCol + currentWidth - Math.max(minWidth, currentWidth - dragOffset.col);

      if (actualNewCol !== currentCol || newWidth !== currentWidth) {
        updates = {
          gridColumn: Math.max(1, actualNewCol),
          gridWidth: newWidth
        };
      }
    } else if (dragMode === 'resize-right') {
      // Right edge: adjust width only (for hero blocks)
      const minWidth = 6; // Half page width
      const newWidth = Math.max(minWidth, Math.min(GRID_COLS - currentCol + 1, currentWidth + dragOffset.col));

      if (newWidth !== currentWidth) {
        updates = {
          gridWidth: newWidth
        };
      }
    }

    if (Object.keys(updates).length > 0) {
      await handleUpdateBlock(block.id, updates);
    }

    setDraggedBlock(null);
    setDragMode(null);
    setDragOffset({ col: 0, row: 0 });
  };

  useEffect(() => {
    if (draggedBlock) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggedBlock, dragStart, dragOffset]);

  const getBlockStyle = (block) => {
    let col = block.grid_column || 1;
    let row = block.grid_row || 1;
    let width = block.grid_width || 12;
    let height = block.grid_height || 1;

    // Apply drag offset for the dragged block
    if (draggedBlock && draggedBlock.id === block.id) {
      if (dragMode === 'move') {
        col = Math.max(1, Math.min(GRID_COLS - width + 1, col + dragOffset.col));
        row = Math.max(1, row + dragOffset.row);
      } else if (dragMode === 'resize') {
        width = Math.max(1, Math.min(GRID_COLS - col + 1, width + dragOffset.col));
        height = Math.max(1, height + dragOffset.row);
      } else if (dragMode === 'resize-vertical') {
        height = Math.max(1, height + dragOffset.row);
      } else if (dragMode === 'resize-left') {
        const minWidth = 6;
        const newWidth = Math.max(minWidth, width - dragOffset.col);
        const actualNewCol = col + width - newWidth;
        col = Math.max(1, actualNewCol);
        width = newWidth;
      } else if (dragMode === 'resize-right') {
        const minWidth = 6;
        width = Math.max(minWidth, Math.min(GRID_COLS - col + 1, width + dragOffset.col));
      } else if (dragMode === 'resize-nw') {
        const newCol = Math.max(1, col + dragOffset.col);
        const newRow = Math.max(1, row + dragOffset.row);
        width = Math.max(1, width - dragOffset.col);
        height = Math.max(1, height - dragOffset.row);
        col = newCol;
        row = newRow;
      } else if (dragMode === 'resize-ne') {
        row = Math.max(1, row + dragOffset.row);
        width = Math.max(1, Math.min(GRID_COLS - col + 1, width + dragOffset.col));
        height = Math.max(1, height - dragOffset.row);
      } else if (dragMode === 'resize-sw') {
        const newCol = Math.max(1, col + dragOffset.col);
        width = Math.max(1, width - dragOffset.col);
        height = Math.max(1, height + dragOffset.row);
        col = newCol;
      }
    }

    return {
      gridColumn: `${col} / span ${width}`,
      gridRow: `${row} / span ${height}`,
      minHeight: `${height * CELL_HEIGHT}px`
    };
  };

  const renderBlock = (block, isHeroSection = false) => {
    const BlockComponent = getBlockComponent(block.block_type);
    if (!BlockComponent) return null;

    const isSelected = selectedBlock === block.id;
    const isDragging = draggedBlock && draggedBlock.id === block.id;
    const isHero = block.block_type === 'hero';

    return (
      <div
        key={block.id}
        className={`relative group ${isDragging ? 'z-50' : 'z-10'} ${isHeroSection ? 'h-full' : ''}`}
        style={isHeroSection ? {} : getBlockStyle(block)}
        onClick={() => setSelectedBlock(block.id)}
      >
        {/* Block Container */}
        <div
          className={`h-full border-2 rounded-lg transition-all ${block.block_type !== 'text' && block.block_type !== 'image' ? 'shadow-md' : ''} ${
            isSelected
              ? 'border-[#7A99D8] shadow-lg'
              : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
          } ${isDragging ? 'opacity-75' : ''}`}
        >
          {/* Block Controls - Top */}
          <div className={`absolute -top-10 left-0 right-0 flex items-center justify-between px-2 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
            <div className="flex items-center gap-1">
              {/* Move Handle - not shown for hero blocks */}
              {!isHero && (
                <button
                  onMouseDown={(e) => handleMouseDown(e, block, 'move')}
                  className="p-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 cursor-move"
                  title="Drag to move"
                >
                  <Move className="w-3.5 h-3.5 text-gray-500" />
                </button>
              )}

              {/* Block Type Label */}
              <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-200 dark:border-gray-700">
                {block.block_type}{isHero ? ' (fixed position)' : ''}
              </span>
            </div>

            <div className="flex items-center gap-1">
              {/* Edit Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBlock(block.id);
                  // Find the block's edit trigger using data attribute
                  const groupElement = e.target.closest('.group');
                  const editTrigger = groupElement?.querySelector('[data-edit-trigger]');
                  if (editTrigger) {
                    editTrigger.click();
                  }
                }}
                className="p-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300"
                title="Edit element"
              >
                <Pencil className="w-3.5 h-3.5 text-blue-500" />
              </button>

              {/* Delete Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteBlock(block.id);
                }}
                className="p-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300"
                title="Delete element"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-500" />
              </button>
            </div>
          </div>

          {/* Block Content */}
          <div className="h-full overflow-auto">
            <BlockComponent
              key={`${block.id}-${block.background_color}-${block.padding}-${JSON.stringify(block.content)}`}
              content={block.content}
              padding={block.padding}
              backgroundColor={block.background_color}
              gridWidth={block.grid_width}
              gridHeight={block.grid_height}
              headingFont={portalSettings?.headingFont}
              bodyFont={portalSettings?.bodyFont}
              onUpdate={(updates) => {
                // For hero blocks, enforce minimum width of half page
                if (isHero) {
                  updates.gridWidth = Math.max(12, updates.gridWidth || 24);
                }
                handleUpdateBlock(block.id, updates);
              }}
              onDelete={() => handleDeleteBlock(block.id)}
            />
          </div>

          {/* Resize Handles - All four corners */}
          {isHero && (
            <>
              {/* Left edge handle */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize-left')}
                className={`absolute left-0 top-1/2 transform -translate-y-1/2 w-4 h-12 cursor-ew-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize width"
              >
                <div className="absolute left-1 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-[#7A99D8] rounded"></div>
              </div>

              {/* Right edge handle */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize-right')}
                className={`absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-12 cursor-ew-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize width"
              >
                <div className="absolute right-1 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-[#7A99D8] rounded"></div>
              </div>

              {/* Bottom edge handle */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize-vertical')}
                className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-4 cursor-ns-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize height"
              >
                <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-[#7A99D8] rounded"></div>
              </div>
            </>
          )}
          {!isHero && (
            <>
              {/* Top-left corner */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize-nw')}
                className={`absolute top-0 left-0 w-4 h-4 cursor-nw-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize"
              >
                <div className="absolute top-0.5 left-0.5 w-2 h-2 border-t-2 border-l-2 border-[#7A99D8]"></div>
              </div>

              {/* Top-right corner */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize-ne')}
                className={`absolute top-0 right-0 w-4 h-4 cursor-ne-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize"
              >
                <div className="absolute top-0.5 right-0.5 w-2 h-2 border-t-2 border-r-2 border-[#7A99D8]"></div>
              </div>

              {/* Bottom-left corner */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize-sw')}
                className={`absolute bottom-0 left-0 w-4 h-4 cursor-sw-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize"
              >
                <div className="absolute bottom-0.5 left-0.5 w-2 h-2 border-b-2 border-l-2 border-[#7A99D8]"></div>
              </div>

              {/* Bottom-right corner */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize')}
                className={`absolute bottom-0 right-0 w-4 h-4 cursor-se-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize"
              >
                <div className="absolute bottom-0.5 right-0.5 w-2 h-2 border-b-2 border-r-2 border-[#7A99D8]"></div>
              </div>
            </>
          )}
          {!isHero && (
            <>
              {/* Left edge handle for non-hero blocks */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize-left')}
                className={`absolute left-0 top-1/2 transform -translate-y-1/2 w-4 h-12 cursor-ew-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize width"
              >
                <div className="absolute left-1 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-[#7A99D8] rounded"></div>
              </div>

              {/* Right edge handle for non-hero blocks */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize-right')}
                className={`absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-12 cursor-ew-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize width"
              >
                <div className="absolute right-1 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-[#7A99D8] rounded"></div>
              </div>

              {/* Bottom edge handle for non-hero blocks */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize-vertical')}
                className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-4 cursor-ns-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize height"
              >
                <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-[#7A99D8] rounded"></div>
              </div>

              {/* Bottom-right corner */}
              <div
                onMouseDown={(e) => handleMouseDown(e, block, 'resize')}
                className={`absolute bottom-0 right-0 w-4 h-4 cursor-se-resize ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                title="Drag to resize"
              >
                <div className="absolute bottom-0.5 right-0.5 w-2 h-2 border-b-2 border-r-2 border-[#7A99D8]"></div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const getBlockComponent = (blockType) => {
    switch (blockType) {
      case 'text':
        return TextBlock;
      case 'image':
        return ImageBlock;
      case 'hero':
        return HeroBlock;
      case 'news':
        return NewsBlock;
      case 'class-schedule':
        return ClassScheduleBlock;
      case 'calendar':
        return CalendarBlock;
      case 'projects':
        return ProjectsBlock;
      case 'documents':
        return DocumentsBlock;
      case 'tutorials':
        return TutorialsBlock;
      default:
        return null;
    }
  };

  const gridRows = getGridRows();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Top Bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (hasUnsavedChanges) {
                    if (confirm('You have unsaved changes. Are you sure you want to leave without saving?')) {
                      onClose();
                    }
                  } else {
                    onClose();
                  }
                }}
                className="p-2 text-gray-400 hover:text-[#7A99D8] rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#3C3C3C] dark:text-gray-100">
                  {pageData.title}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  /{pageData.slug}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Save Draft Button */}
              <button
                onClick={handleSaveDraft}
                disabled={saving}
                className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  saved
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                } disabled:bg-blue-400`}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Draft'}
              </button>

              {/* Preview Draft Button */}
              <button
                onClick={() => window.open(`/portal/${pageData.user_id}/${pageData.slug}?preview=true`, '_blank')}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Draft
              </button>

              {/* Publish Button */}
              <button
                onClick={() => setShowPublishConfirm(true)}
                disabled={publishing}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:bg-purple-400"
              >
                <Upload className="w-4 h-4 mr-2" />
                {publishing ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation - Styled like action buttons */}
      <div className="px-6 pt-6">
        <div className="mb-6">
          <nav className="flex space-x-2">
            <button
              onClick={() => setShowAddBlockModal(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Element
            </button>
            <button
              onClick={() => setShowGrid(!showGrid)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
            >
              <Grid className="w-4 h-4 mr-2" />
              {showGrid ? 'Grid On' : 'Grid Off'}
            </button>
            <button
              onClick={() => setShowPageSettings(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </button>
          </nav>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Browser Frame Container */}
      <div className="max-w-6xl mx-auto px-6 pb-8">
        {/* Browser Chrome */}
        <div className="bg-gray-200 dark:bg-gray-700 rounded-t-lg px-4 py-3 flex items-center gap-2">
          {/* Traffic lights */}
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          {/* URL bar */}
          <div className="flex-1 ml-4">
            <div className="bg-white dark:bg-gray-800 rounded px-3 py-1 text-xs text-gray-500 dark:text-gray-400 truncate">
              {pageData.is_published
                ? `${window.location.origin}/portal/${pageData.user_id}/${pageData.slug}`
                : 'Page not published yet'
              }
            </div>
          </div>
        </div>

        {/* Browser Content Area */}
        <div
          className="border-x border-b border-gray-300 dark:border-gray-600 rounded-b-lg overflow-hidden"
          style={{ maxHeight: '80vh', overflowY: 'auto' }}
        >
          {/* Page Content with Background */}
          <div
            className="min-h-[600px] relative"
            style={{
              fontFamily: portalSettings?.bodyFont && portalSettings.bodyFont !== 'system'
                ? `"${portalSettings.bodyFont}", sans-serif`
                : 'ui-sans-serif, system-ui, sans-serif',
              backgroundColor: pageData.background_type === 'color' && pageData.background_color ? pageData.background_color : '#ffffff'
            }}
          >
            {/* Background Image Layer with Opacity */}
            {pageData.background_type === 'image' && pageData.background_image && (
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${pageData.background_image})`,
                  backgroundRepeat: pageData.background_position === 'repeat' ? 'repeat' : 'no-repeat',
                  backgroundSize: (() => {
                    const size = pageData.background_size || 100;
                    if (pageData.background_position === 'stretch') return '100% 100%';
                    if (pageData.background_position === 'repeat') return `${size}%`;
                    return size === 100 ? 'cover' : `${size}%`;
                  })(),
                  backgroundPosition: pageData.background_position === 'stretch' || pageData.background_position === 'repeat' ? 'initial' : 'center',
                  opacity: pageData.background_opacity !== undefined ? pageData.background_opacity / 100 : 1,
                  zIndex: 0,
                  pointerEvents: 'none'
                }}
              />
            )}

            {/* Content Layer */}
            <div className="relative" style={{ zIndex: 1 }}>
            {/* Header Preview (non-editable) - Matches PortalLayout */}
            <div
              className="shadow-md border-b border-gray-200 relative z-10"
              style={{ backgroundColor: portalSettings?.headerBackgroundColor || '#ffffff' }}
            >
              <div className="absolute top-2 right-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded z-10">
                Header (Edit in Portal Settings)
              </div>
          <div
            className="px-4"
            style={{
              paddingTop: portalSettings?.headerHeight === 'small' ? '12px' : portalSettings?.headerHeight === 'large' ? '32px' : '24px',
              paddingBottom: portalSettings?.headerHeight === 'small' ? '12px' : portalSettings?.headerHeight === 'large' ? '32px' : '24px'
            }}
          >
            {/* Header Content */}
            {portalSettings?.headerLayout === 'centered' ? (
              /* Centered Layout */
              <div className="mb-4 relative">
                {/* Social Icons - Top Right */}
                <div className="absolute top-0 right-0 flex items-center gap-2">
                  {portalSettings?.showSocialMedia && portalSettings?.socialMedia && (
                    <div className="flex items-center gap-2">
                      {portalSettings.socialMedia.facebook?.enabled && (
                        <span className="text-gray-500 cursor-pointer">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
                          </svg>
                        </span>
                      )}
                      {portalSettings.socialMedia.instagram?.enabled && (
                        <span className="text-gray-500 cursor-pointer">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                            <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/>
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                          </svg>
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Centered Logo */}
                <div className="flex flex-col items-center">
                  <div className="mb-4">
                    {portalSettings?.logo ? (
                      <img
                        src={portalSettings.logo}
                        alt="Logo"
                        className="object-contain"
                        style={{
                          maxHeight: portalSettings?.logoSize === 'xsmall' ? '60px' :
                                     portalSettings?.logoSize === 'small' ? '80px' :
                                     portalSettings?.logoSize === 'large' ? '120px' :
                                     portalSettings?.logoSize === 'xlarge' ? '140px' : '100px',
                          height: 'auto',
                          width: 'auto'
                        }}
                      />
                    ) : (
                      <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-3xl font-bold text-gray-600">
                          {(portalSettings?.businessName || 'C').charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Centered Navigation */}
                  <nav>
                    <ul className="flex flex-wrap justify-center gap-6 text-sm uppercase tracking-wide" style={{ fontFamily: portalSettings?.navFont && portalSettings.navFont !== 'system' ? `'${portalSettings.navFont}', sans-serif` : 'inherit' }}>
                      {navPages.map(navPage => (
                        <li key={navPage.id}>
                          <span
                            className="transition-colors cursor-pointer font-semibold"
                            style={{
                              color: navPage.id === pageData?.id
                                ? (portalSettings?.navActiveIndicatorColor || '#3b82f6')
                                : (portalSettings?.navFontColor || '#4b5563'),
                              borderBottom: navPage.id === pageData?.id
                                ? `2px solid ${portalSettings?.navActiveIndicatorColor || '#3b82f6'}`
                                : '2px solid transparent',
                              paddingBottom: '4px'
                            }}
                          >
                            {navPage.title}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </div>
            ) : (
              /* Standard Layout */
              <div className="flex items-end justify-between mb-4 relative">
                {/* Logo - Left Side */}
                <div style={{ transform: 'translateY(20px)' }}>
                  {portalSettings?.logo ? (
                    <img
                      src={portalSettings.logo}
                      alt="Logo"
                      className="object-contain"
                      style={{
                        maxHeight: portalSettings?.logoSize === 'xsmall' ? '60px' :
                                   portalSettings?.logoSize === 'small' ? '80px' :
                                   portalSettings?.logoSize === 'large' ? '120px' :
                                   portalSettings?.logoSize === 'xlarge' ? '140px' : '100px',
                        height: 'auto',
                        width: 'auto'
                      }}
                    />
                  ) : (
                    <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-3xl font-bold text-gray-600">
                        {(portalSettings?.businessName || 'C').charAt(0)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Navigation Menu - Center */}
                <nav className="flex-1 flex justify-center">
                  <ul className="flex flex-wrap gap-6 text-sm uppercase tracking-wide" style={{ fontFamily: portalSettings?.navFont && portalSettings.navFont !== 'system' ? `'${portalSettings.navFont}', sans-serif` : 'inherit' }}>
                    {navPages.map(navPage => (
                      <li key={navPage.id}>
                        <span
                          className="transition-colors cursor-pointer font-semibold"
                          style={{
                            color: navPage.id === pageData?.id
                              ? (portalSettings?.navActiveIndicatorColor || '#3b82f6')
                              : (portalSettings?.navFontColor || '#4b5563'),
                            borderBottom: navPage.id === pageData?.id
                              ? `2px solid ${portalSettings?.navActiveIndicatorColor || '#3b82f6'}`
                              : '2px solid transparent',
                            paddingBottom: '4px'
                          }}
                        >
                          {navPage.title}
                        </span>
                      </li>
                    ))}
                  </ul>
                </nav>

                {/* Contact Button and Social Icons - Right Side */}
                <div className="flex items-center gap-2 sm:gap-3">
                  {portalSettings?.contactEmail && (
                    <span className="text-xs text-gray-500 border border-gray-300 px-2 sm:px-3 py-1 rounded whitespace-nowrap">
                      Join My Mailing List
                    </span>
                  )}

                {/* Social Media Icons */}
                {portalSettings?.showSocialMedia && portalSettings?.socialMedia && (
                  <div className="flex items-center gap-2">
                    {portalSettings.socialMedia.facebook?.enabled && (
                      <span className="text-gray-500 hover:text-pink-500 transition-colors cursor-pointer">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
                        </svg>
                      </span>
                    )}
                    {portalSettings.socialMedia.instagram?.enabled && (
                      <span className="text-gray-500 hover:text-pink-500 transition-colors cursor-pointer">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                          <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/>
                          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                        </svg>
                      </span>
                    )}
                    {portalSettings.socialMedia.twitter?.enabled && (
                      <span className="text-gray-500 hover:text-pink-500 transition-colors cursor-pointer">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
                        </svg>
                      </span>
                    )}
                    {portalSettings.socialMedia.youtube?.enabled && (
                      <span className="text-gray-500 hover:text-pink-500 transition-colors cursor-pointer">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.33z"/>
                          <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
                        </svg>
                      </span>
                    )}
                    {portalSettings.socialMedia.pinterest?.enabled && (
                      <span className="text-gray-500 hover:text-pink-500 transition-colors cursor-pointer">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
                        </svg>
                      </span>
                    )}
                    {portalSettings.socialMedia.tiktok?.enabled && (
                      <span className="text-gray-500 hover:text-pink-500 transition-colors cursor-pointer">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                        </svg>
                      </span>
                    )}
                    {portalSettings.socialMedia.linkedin?.enabled && (
                      <span className="text-gray-500 hover:text-pink-500 transition-colors cursor-pointer">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </span>
                    )}
                  </div>
                )}
                </div>
              </div>
            )}

            {/* Divider Line */}
            <div className="border-t border-gray-200"></div>
          </div>
        </div>

            {/* Main Content Area with Grid Background */}
            <div className="px-4 -mt-8 pb-6 relative">
              {/* Single unified grid that includes hero area */}
              <div
                ref={gridRef}
                className="relative"
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
                  gridAutoRows: `minmax(${CELL_HEIGHT}px, auto)`,
                  gap: '4px',
                  minHeight: `${(gridRows + (blocks.some(b => b.block_type === 'hero') ? Math.max(...blocks.filter(b => b.block_type === 'hero').map(b => b.grid_height || 3)) : 0)) * CELL_HEIGHT}px`
                }}
              >
                {/* Grid Lines Background */}
                {showGrid && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `
                        linear-gradient(to right, rgba(244, 114, 182, 0.5) 1px, transparent 1px),
                        linear-gradient(to bottom, rgba(244, 114, 182, 0.5) 1px, transparent 1px)
                      `,
                      backgroundSize: `${100 / GRID_COLS}% ${CELL_HEIGHT}px`
                    }}
                  />
                )}

                {/* Hero blocks rendered in the grid */}
                {blocks.filter(b => b.block_type === 'hero').map((heroBlock) => {
                  // Calculate hero dimensions with drag offset
                  let heroWidth = heroBlock.grid_width || 12;
                  let heroCol = heroBlock.grid_column || 1;
                  let heroHeight = heroBlock.grid_height || 3;
                  let heroRow = 1; // Hero always starts at row 1

                  if (draggedBlock && draggedBlock.id === heroBlock.id) {
                    if (dragMode === 'resize-vertical') {
                      heroHeight = Math.max(1, heroHeight + dragOffset.row);
                    } else if (dragMode === 'resize-left') {
                      const minWidth = 6;
                      const newWidth = Math.max(minWidth, heroWidth - dragOffset.col);
                      heroCol = heroCol + heroWidth - newWidth;
                      heroCol = Math.max(1, heroCol);
                      heroWidth = newWidth;
                    } else if (dragMode === 'resize-right') {
                      const minWidth = 6;
                      heroWidth = Math.max(minWidth, Math.min(GRID_COLS - heroCol + 1, heroWidth + dragOffset.col));
                    }
                  }

                  // Use aspect-ratio to maintain consistent proportions between builder and published site
                  // Ratio based on 48 columns width to height in rows (each row = 20px equivalent)
                  const aspectRatio = (heroWidth / GRID_COLS) / (heroHeight * CELL_HEIGHT / 1000);

                  return (
                    <div
                      key={heroBlock.id}
                      className="relative z-0"
                      style={{
                        gridColumn: `${heroCol} / span ${heroWidth}`,
                        gridRow: `${heroRow} / span ${heroHeight}`,
                        aspectRatio: `${heroWidth} / ${heroHeight * 1.5}`
                      }}
                    >
                      {renderBlock(heroBlock, true)}
                    </div>
                  );
                })}

                {/* Other blocks rendered in the grid */}
                {blocks.filter(b => b.block_type !== 'hero').map((block) => renderBlock(block))}
              </div>

          {/* Empty State */}
          {blocks.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <Grid className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100 mb-2">
                  No content elements yet
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Click "Add Element" to start building this page
                </p>
              </div>
            </div>
          )}
            </div>

            {/* Footer Preview (non-editable) - Matches PortalLayout */}
            {(() => {
              // Color scheme configurations matching PortalLayout
              const colorSchemes = {
                craft: { footer: 'bg-craft-dark', footerText: 'text-white' },
                summer: { footer: 'bg-summer-dark', footerText: 'text-white' },
                honestly: { footer: 'bg-honestly-primary', footerText: 'text-white' },
                sweets: { footer: 'bg-sweets-secondary', footerText: 'text-white' },
                fabled: { footer: 'bg-fabled-accent', footerText: 'text-white' }
              };
              const currentColors = colorSchemes[portalSettings?.colorScheme] || colorSchemes.craft;
              const getFontFamily = (fontName) => {
                if (!fontName || fontName === 'system') return 'ui-sans-serif, system-ui, sans-serif';
                return `"${fontName}", sans-serif`;
              };
              return (
            <div className={`${currentColors.footer} ${currentColors.footerText} border-t border-gray-700 py-6 px-4 relative`}>
              <div className="absolute top-2 right-2 text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded z-10">
                Footer (Edit in Portal Settings)
              </div>

              <div className="grid md:grid-cols-3 gap-6 text-sm">
                {/* Contact Information */}
                <div>
                  <h3 className="font-semibold mb-3" style={{ fontFamily: getFontFamily(portalSettings?.headingFont) }}>Contact Information</h3>
                  {portalSettings?.contactEmail && (
                    <p className="text-gray-300 mb-1">
                      <span className="font-medium">Email:</span> {portalSettings.contactEmail}
                    </p>
                  )}
                  {portalSettings?.contactPhone && (
                    <p className="text-gray-300 mb-1">
                      <span className="font-medium">Phone:</span> {portalSettings.contactPhone}
                    </p>
                  )}
                  {portalSettings?.address && (
                    <p className="text-gray-300">
                      <span className="font-medium">Address:</span> {portalSettings.address}
                    </p>
                  )}
                  {!portalSettings?.contactEmail && !portalSettings?.contactPhone && !portalSettings?.address && (
                    <p className="text-gray-400 text-xs italic">No contact info set</p>
                  )}
                </div>

                {/* Quick Links */}
                <div>
                  <h3 className="font-semibold mb-3" style={{ fontFamily: getFontFamily(portalSettings?.headingFont) }}>Quick Links</h3>
                  <ul className="space-y-1 text-gray-300">
                    {navPages.slice(0, 5).map((navPage) => (
                      <li key={navPage.id}>
                        <span className="hover:text-white transition-colors cursor-pointer">
                          {navPage.title}
                        </span>
                      </li>
                    ))}
                    {navPages.length === 0 && (
                      <li className="text-gray-400 text-xs italic">No pages yet</li>
                    )}
                  </ul>
                </div>

                {/* Powered By */}
                <div>
                  <h3 className="font-semibold mb-3" style={{ fontFamily: getFontFamily(portalSettings?.headingFont) }}>Powered By</h3>
                  <p className="text-gray-300">
                    <span className="hover:text-white transition-colors">
                      ClassTraks
                    </span>
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    Simplifying class management
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-700 mt-6 pt-3 text-center text-gray-400 text-xs">
                <p>&copy; 2025 {portalSettings?.businessName || 'Your Business'}. All rights reserved.</p>
              </div>
            </div>
              );
            })()}
            </div>
            {/* End Content Layer */}
          </div>
        </div>
      </div>

      {/* Page Settings Modal */}
      {showPageSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">
                Page Settings
              </h3>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                  Page Title
                </label>
                <input
                  type="text"
                  value={pageData.title}
                  onChange={(e) => setPageData({ ...pageData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                  URL Slug
                </label>
                <input
                  type="text"
                  value={pageData.slug}
                  onChange={(e) => setPageData({ ...pageData, slug: e.target.value.toLowerCase() })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                />
              </div>

              {/* Meta Description */}
              <div>
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                  Meta Description
                </label>
                <textarea
                  value={pageData.meta_description || ''}
                  onChange={(e) => setPageData({ ...pageData, meta_description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                  rows="2"
                />
              </div>

              {/* Options */}
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={pageData.is_published || false}
                    onChange={(e) => setPageData({ ...pageData, is_published: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-[#3C3C3C] dark:text-gray-100">
                    Published
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={pageData.show_in_navigation || false}
                    onChange={(e) => setPageData({ ...pageData, show_in_navigation: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-[#3C3C3C] dark:text-gray-100">
                    Show in navigation
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={pageData.is_home_page || false}
                    onChange={(e) => setPageData({ ...pageData, is_home_page: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-[#3C3C3C] dark:text-gray-100">
                    Set as home page
                  </span>
                </label>
              </div>

              {/* Background Settings */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                  Page Background
                </label>
                <select
                  value={pageData.background_type || 'none'}
                  onChange={(e) => setPageData({ ...pageData, background_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100 mb-3"
                >
                  <option value="none">None</option>
                  <option value="color">Solid Color</option>
                  <option value="image">Image</option>
                </select>

                {pageData.background_type === 'color' && (
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Background Color
                    </label>
                    <input
                      type="color"
                      value={pageData.background_color || '#ffffff'}
                      onChange={(e) => setPageData({ ...pageData, background_color: e.target.value })}
                      className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                    />
                  </div>
                )}

                {pageData.background_type === 'image' && (
                  <div className="space-y-3">
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                      {pageData.background_image ? (
                        <div className="space-y-3">
                          <img src={pageData.background_image} alt="Background" className="max-w-full h-auto max-h-24 mx-auto rounded" />
                          <div className="flex justify-center gap-2">
                            <label className="inline-flex items-center gap-2 px-3 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 text-xs">
                              Change
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleBgImageUpload}
                                className="hidden"
                                disabled={uploadingBg}
                              />
                            </label>
                            <button
                              onClick={() => setPageData({ ...pageData, background_image: '' })}
                              className="px-3 py-1 text-red-600 hover:text-red-700 text-xs"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className="cursor-pointer block text-center">
                          <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {uploadingBg ? 'Uploading...' : 'Click to upload'}
                          </p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleBgImageUpload}
                            className="hidden"
                            disabled={uploadingBg}
                          />
                        </label>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Position
                      </label>
                      <select
                        value={pageData.background_position || 'fit'}
                        onChange={(e) => setPageData({ ...pageData, background_position: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100 text-sm"
                      >
                        <option value="fit">Fit (cover)</option>
                        <option value="stretch">Stretch</option>
                        <option value="repeat">Repeat (tile)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Size (%)
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="200"
                        value={pageData.background_size || 100}
                        onChange={(e) => setPageData({ ...pageData, background_size: parseInt(e.target.value) })}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>10%</span>
                        <span>{pageData.background_size || 100}%</span>
                        <span>200%</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Opacity (%)
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={pageData.background_opacity !== undefined ? pageData.background_opacity : 100}
                        onChange={(e) => setPageData({ ...pageData, background_opacity: parseInt(e.target.value) })}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>0%</span>
                        <span>{pageData.background_opacity !== undefined ? pageData.background_opacity : 100}%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                )}

                {pageData.background_type !== 'none' && pageData.is_home_page && (
                  <label className="flex items-center gap-2 mt-3">
                    <input
                      type="checkbox"
                      checked={pageData.apply_background_to_all || false}
                      onChange={(e) => setPageData({ ...pageData, apply_background_to_all: e.target.checked })}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    <span className="text-sm text-[#3C3C3C] dark:text-gray-100">
                      Apply to all pages
                    </span>
                  </label>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 bg-white dark:bg-gray-800 p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setShowPageSettings(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleUpdatePageSettings({
                    title: pageData.title,
                    slug: pageData.slug,
                    metaDescription: pageData.meta_description,
                    isPublished: pageData.is_published,
                    showInNavigation: pageData.show_in_navigation,
                    isHomePage: pageData.is_home_page,
                    backgroundType: pageData.background_type,
                    backgroundColor: pageData.background_color,
                    backgroundImage: pageData.background_image,
                    backgroundPosition: pageData.background_position,
                    backgroundSize: pageData.background_size,
                    backgroundOpacity: pageData.background_opacity,
                    applyBackgroundToAll: pageData.apply_background_to_all
                  });
                  setShowPageSettings(false);
                }}
                className="px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8] flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Element Modal */}
      {showAddBlockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">
                {newBlockType ? `Add ${BLOCK_TYPES.find(b => b.type === newBlockType)?.label}` : 'Add Content Element'}
              </h3>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {!newBlockType ? (
                /* Element Type Selection */
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {BLOCK_TYPES.map((blockType) => (
                    <button
                      key={blockType.type}
                      onClick={() => handleSelectBlockType(blockType.type)}
                      className="flex flex-col items-center gap-2 p-4 text-center rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-[#7A99D8] hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors"
                    >
                      <blockType.icon className="w-8 h-8 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100">
                          {blockType.label}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {blockType.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                /* Block Configuration */
                <div className="space-y-6">
                  {/* Block Settings - Hide for class-schedule as it has its own Appearance section */}
                  {newBlockType !== 'class-schedule' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Padding
                        </label>
                        <select
                          value={newBlockSettings.padding}
                          onChange={(e) => setNewBlockSettings({ ...newBlockSettings, padding: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                        >
                          <option value="none">None</option>
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Background Color
                        </label>
                        <input
                          type="color"
                          value={newBlockSettings.backgroundColor || '#ffffff'}
                          onChange={(e) => setNewBlockSettings({ ...newBlockSettings, backgroundColor: e.target.value })}
                          className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                        />
                      </div>
                    </div>
                  )}

                  {/* Block-specific content settings */}
                  {newBlockType === 'text' && (
                    <div className="space-y-4">
                      {/* Font Selection */}
                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Font Family
                        </label>
                        <select
                          value={newBlockContent.fontFamily || 'system'}
                          onChange={(e) => setNewBlockContent({ ...newBlockContent, fontFamily: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                        >
                          <option value="system">System Default</option>
                          <option value="Coustard">Coustard</option>
                          <option value="Alfa Slab One">Alfa Slab One</option>
                          <option value="Ovo">Ovo</option>
                          <option value="Fraunces">Fraunces</option>
                          <option value="Lancelot">Lancelot</option>
                          <option value="Arvo">Arvo</option>
                          <option value="Josefin Slab">Josefin Slab</option>
                          <option value="Outfit">Outfit</option>
                          <option value="Cal Sans">Cal Sans</option>
                          <option value="Tilt Warp">Tilt Warp</option>
                          <option value="McLaren">McLaren</option>
                          <option value="Mate">Mate</option>
                          <option value="Buda">Buda</option>
                          <option value="Bellefair">Bellefair</option>
                          <option value="Questrial">Questrial</option>
                          <option value="DM Mono">DM Mono</option>
                          <option value="Quicksand">Quicksand</option>
                          <option value="Nunito Sans">Nunito Sans</option>
                          <option value="Roboto">Roboto</option>
                          <option value="Manrope">Manrope</option>
                          <option value="Jost">Jost</option>
                        </select>
                      </div>

                      {/* Rich Text Editor with live styling */}
                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Content
                        </label>
                        <style>
                          {`
                            .add-text-editor .ql-editor {
                              font-family: ${newBlockContent.fontFamily && newBlockContent.fontFamily !== 'system'
                                ? `"${newBlockContent.fontFamily}", sans-serif`
                                : 'ui-sans-serif, system-ui, sans-serif'};
                              background-color: ${newBlockSettings.backgroundColor || '#ffffff'};
                              min-height: 150px;
                            }
                            .add-text-editor .ql-snow .ql-picker.ql-size .ql-picker-label::before,
                            .add-text-editor .ql-snow .ql-picker.ql-size .ql-picker-item::before {
                              content: attr(data-value) !important;
                            }
                            .add-text-editor .ql-snow .ql-picker.ql-size .ql-picker-label[data-value=""]::before,
                            .add-text-editor .ql-snow .ql-picker.ql-size .ql-picker-item[data-value=""]::before {
                              content: 'Normal' !important;
                            }
                          `}
                        </style>
                        <ReactQuill
                          theme="snow"
                          value={newBlockContent.html || ''}
                          onChange={(value) => setNewBlockContent({ ...newBlockContent, html: value })}
                          modules={{
                            toolbar: [
                              [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                              [{ 'size': ['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '30px', '36px', '48px', '72px'] }],
                              ['bold', 'italic', 'underline', 'strike'],
                              [{ 'color': [] }, { 'background': [] }],
                              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                              [{ 'align': [] }],
                              ['link'],
                              ['clean']
                            ],
                          }}
                          className="bg-white dark:bg-gray-700 rounded add-text-editor"
                        />
                      </div>
                    </div>
                  )}

                  {newBlockType === 'image' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Upload Image
                        </label>
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
                          {newBlockContent.url ? (
                            <div className="space-y-4">
                              <img
                                src={newBlockContent.url}
                                alt="Preview"
                                className="max-w-full h-auto max-h-48 mx-auto rounded"
                              />
                              <div className="flex justify-center gap-2">
                                <label className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 text-sm">
                                  Change Image
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                    disabled={uploading}
                                  />
                                </label>
                                <button
                                  onClick={() => setNewBlockContent({ ...newBlockContent, url: '' })}
                                  className="px-4 py-2 text-red-600 hover:text-red-700 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="cursor-pointer block text-center">
                              <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {uploading ? 'Uploading...' : 'Click to upload an image'}
                              </p>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Alt Text (for accessibility)
                        </label>
                        <input
                          type="text"
                          value={newBlockContent.alt || ''}
                          onChange={(e) => setNewBlockContent({ ...newBlockContent, alt: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                          placeholder="Description of the image"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Caption (optional)
                        </label>
                        <input
                          type="text"
                          value={newBlockContent.caption || ''}
                          onChange={(e) => setNewBlockContent({ ...newBlockContent, caption: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                          placeholder="Image caption"
                        />
                      </div>
                    </div>
                  )}

                  {newBlockType === 'hero' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Background Image
                        </label>
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
                          {newBlockContent.backgroundImage ? (
                            <div className="space-y-4">
                              <img
                                src={newBlockContent.backgroundImage}
                                alt="Preview"
                                className="max-w-full h-auto max-h-48 mx-auto rounded"
                              />
                              <div className="flex justify-center gap-2">
                                <label className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 text-sm">
                                  Change Image
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={async (e) => {
                                      const file = e.target.files[0];
                                      if (!file) return;
                                      setUploading(true);
                                      try {
                                        const formData = new FormData();
                                        formData.append('file', file);
                                        const response = await api.request('/upload/single', { method: 'POST', body: formData });
                                        const imageUrl = response.file?.url || response.url;
                                        setNewBlockContent({ ...newBlockContent, backgroundImage: imageUrl });
                                      } catch (error) {
                                        console.error('Error uploading image:', error);
                                        alert('Failed to upload image');
                                      } finally {
                                        setUploading(false);
                                      }
                                    }}
                                    className="hidden"
                                    disabled={uploading}
                                  />
                                </label>
                                <button
                                  onClick={() => setNewBlockContent({ ...newBlockContent, backgroundImage: '' })}
                                  className="px-4 py-2 text-red-600 hover:text-red-700 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="cursor-pointer block text-center">
                              <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {uploading ? 'Uploading...' : 'Click to upload hero image'}
                              </p>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  setUploading(true);
                                  try {
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    const response = await api.request('/upload/single', { method: 'POST', body: formData });
                                    const imageUrl = response.file?.url || response.url;
                                    setNewBlockContent({ ...newBlockContent, backgroundImage: imageUrl });
                                  } catch (error) {
                                    console.error('Error uploading image:', error);
                                    alert('Failed to upload image');
                                  } finally {
                                    setUploading(false);
                                  }
                                }}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Overlay Text
                        </label>
                        <textarea
                          value={newBlockContent.overlayText || ''}
                          onChange={(e) => setNewBlockContent({ ...newBlockContent, overlayText: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                          rows="3"
                          placeholder="Welcome to our studio!"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                            Text Placement
                          </label>
                          <select
                            value={newBlockContent.textPlacement || 'middle'}
                            onChange={(e) => setNewBlockContent({ ...newBlockContent, textPlacement: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                          >
                            <option value="top">Top</option>
                            <option value="middle">Middle</option>
                            <option value="bottom">Bottom</option>
                          </select>
                        </div>

                        <div className="flex items-end gap-2">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={newBlockContent.textBgEnabled || false}
                              onChange={(e) => setNewBlockContent({ ...newBlockContent, textBgEnabled: e.target.checked })}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm text-[#3C3C3C] dark:text-gray-100">Text Background</span>
                          </label>
                          {newBlockContent.textBgEnabled && (
                            <input
                              type="color"
                              value={newBlockContent.textBgColor || '#000000'}
                              onChange={(e) => setNewBlockContent({ ...newBlockContent, textBgColor: e.target.value })}
                              className="w-8 h-8 rounded border border-gray-300"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {newBlockType === 'class-schedule' && (
                    <div className="space-y-6">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Section Title
                        </label>
                        <input
                          type="text"
                          value={newBlockContent.title || 'Upcoming Classes'}
                          onChange={(e) => setNewBlockContent({ ...newBlockContent, title: e.target.value })}
                          placeholder="Upcoming Classes"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                        />
                      </div>

                      {/* Font Family */}
                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Font Family
                        </label>
                        <select
                          value={newBlockContent.fontFamily || 'system'}
                          onChange={(e) => setNewBlockContent({ ...newBlockContent, fontFamily: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                          style={{ fontFamily: getFontFamily(newBlockContent.fontFamily || 'system') }}
                        >
                          {FONTS.map(font => (
                            <option key={font.value} value={font.value} style={{ fontFamily: getFontFamily(font.value) }}>
                              {font.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Months to display */}
                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Number of Months to Display
                        </label>
                        <select
                          value={newBlockContent.months || 3}
                          onChange={(e) => setNewBlockContent({ ...newBlockContent, months: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                        >
                          <option value={1}>1 Month</option>
                          <option value={2}>2 Months</option>
                          <option value={3}>3 Months</option>
                          <option value={4}>4 Months</option>
                          <option value={5}>5 Months</option>
                          <option value={6}>6 Months</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Shows classes starting within this time period</p>
                      </div>

                      {/* Include Current Month */}
                      <div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newBlockContent.includeCurrentMonth || false}
                            onChange={(e) => setNewBlockContent({ ...newBlockContent, includeCurrentMonth: e.target.checked })}
                            className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 rounded focus:ring-[#7A99D8] focus:ring-2"
                          />
                          <span className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100">
                            Include Current Month
                          </span>
                        </label>
                        <p className="text-xs text-gray-500 mt-1 ml-6">
                          If unchecked, only future months will be shown
                        </p>
                      </div>

                      {/* Filter Options */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h4 className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">
                          Filter Classes
                        </h4>

                        {/* Category Filter */}
                        <div className="mb-3">
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Category
                          </label>
                          <input
                            type="text"
                            value={newBlockContent.categoryFilter || ''}
                            onChange={(e) => setNewBlockContent({ ...newBlockContent, categoryFilter: e.target.value })}
                            placeholder="e.g., Sewing, Quilting"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                          />
                        </div>

                        {/* Skill Level Filter */}
                        <div className="mb-3">
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Skill Level
                          </label>
                          <input
                            type="text"
                            value={newBlockContent.skillLevelFilter || ''}
                            onChange={(e) => setNewBlockContent({ ...newBlockContent, skillLevelFilter: e.target.value })}
                            placeholder="e.g., Beginner, Intermediate"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                          />
                        </div>

                        {/* Instructor Filter */}
                        <div className="mb-3">
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Instructor
                          </label>
                          <input
                            type="text"
                            value={newBlockContent.instructorFilter || ''}
                            onChange={(e) => setNewBlockContent({ ...newBlockContent, instructorFilter: e.target.value })}
                            placeholder="Instructor name"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                          />
                        </div>

                        {/* Tags Filter */}
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Tags (comma-separated)
                          </label>
                          <input
                            type="text"
                            value={newBlockContent.tagsFilter || ''}
                            onChange={(e) => setNewBlockContent({ ...newBlockContent, tagsFilter: e.target.value.split(',').map(t => t.trim()).filter(t => t) })}
                            placeholder="e.g., kids, adults, beginner"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Filter classes that have any of these tags
                          </p>
                        </div>
                      </div>

                      {/* Appearance Settings */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h4 className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">
                          Appearance
                        </h4>

                        {/* Padding */}
                        <div className="mb-3">
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Padding
                          </label>
                          <select
                            value={newBlockSettings.padding}
                            onChange={(e) => setNewBlockSettings({ ...newBlockSettings, padding: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                          >
                            <option value="none">None</option>
                            <option value="small">Small</option>
                            <option value="medium">Medium</option>
                            <option value="large">Large</option>
                          </select>
                        </div>

                        {/* Background Color */}
                        <div className="mb-3">
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Container Background
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={newBlockSettings.backgroundColor || '#ffffff'}
                              onChange={(e) => setNewBlockSettings({ ...newBlockSettings, backgroundColor: e.target.value })}
                              className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg"
                            />
                            <button
                              onClick={() => setNewBlockSettings({ ...newBlockSettings, backgroundColor: '' })}
                              className={`text-xs whitespace-nowrap ${newBlockSettings.backgroundColor ? 'text-gray-500 hover:text-red-500' : 'text-gray-300'}`}
                            >
                              {newBlockSettings.backgroundColor ? 'Clear' : 'No Color'}
                            </button>
                          </div>
                        </div>

                        {/* Card Background Color */}
                        <div className="mb-3">
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Card Background
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={newBlockContent.cardBackgroundColor || '#ffffff'}
                              onChange={(e) => setNewBlockContent({ ...newBlockContent, cardBackgroundColor: e.target.value })}
                              className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg"
                            />
                            <button
                              onClick={() => setNewBlockContent({ ...newBlockContent, cardBackgroundColor: '#ffffff' })}
                              className="text-xs text-gray-500 hover:text-red-500 whitespace-nowrap"
                            >
                              Reset
                            </button>
                          </div>
                        </div>

                        {/* Price Button Color */}
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Price Button Color
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={newBlockContent.priceButtonColor || '#e5e7eb'}
                              onChange={(e) => setNewBlockContent({ ...newBlockContent, priceButtonColor: e.target.value })}
                              className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg"
                            />
                            <button
                              onClick={() => setNewBlockContent({ ...newBlockContent, priceButtonColor: '#e5e7eb' })}
                              className="text-xs text-gray-500 hover:text-red-500 whitespace-nowrap"
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {newBlockType === 'background' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Background Image
                        </label>
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
                          {newBlockContent.url ? (
                            <div className="space-y-4">
                              <img
                                src={newBlockContent.url}
                                alt="Preview"
                                className="max-w-full h-auto max-h-32 mx-auto rounded"
                              />
                              <div className="flex justify-center gap-2">
                                <label className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 text-sm">
                                  Change Image
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                    disabled={uploading}
                                  />
                                </label>
                                <button
                                  onClick={() => setNewBlockContent({ ...newBlockContent, url: '' })}
                                  className="px-4 py-2 text-red-600 hover:text-red-700 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="cursor-pointer block text-center">
                              <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {uploading ? 'Uploading...' : 'Click to upload background image'}
                              </p>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                          Position
                        </label>
                        <select
                          value={newBlockContent.position || 'fit'}
                          onChange={(e) => setNewBlockContent({ ...newBlockContent, position: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                        >
                          <option value="fit">Fit - Scale to fit (maintains aspect ratio)</option>
                          <option value="stretch">Stretch - Fill entire area (may distort)</option>
                          <option value="repeat">Repeat - Tile the image</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {newBlockType === 'news' && (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        News stories can be configured after adding the block. Click on the block to edit stories.
                      </p>
                    </div>
                  )}

                  {newBlockType === 'projects' && (
                    <div className="grid grid-cols-2 gap-6">
                      {/* LEFT COLUMN - Available Projects */}
                      <div>
                        <h4 className="font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">
                          Available Projects ({availableProjects.length})
                        </h4>
                        {loadingProjects ? (
                          <div className="text-center py-8 text-gray-500">Loading projects...</div>
                        ) : availableProjects.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">No projects available</div>
                        ) : (
                          <div className="space-y-2 max-h-[500px] overflow-y-auto">
                            {availableProjects.map(project => {
                              const selectedProjects = newBlockContent.selectedProjects || [];
                              const selected = selectedProjects.some(p => p.projectId === project.id);
                              return (
                                <div
                                  key={project.id}
                                  className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors ${
                                    selected
                                      ? 'border-[#7A99D8] bg-blue-50 dark:bg-blue-900/20'
                                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                  }`}
                                  onClick={() => handleProjectToggle(project.id)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => {}}
                                    className="mt-1"
                                  />
                                  {project.main_image_url && (
                                    <img
                                      src={project.main_image_url}
                                      alt={project.name}
                                      className="w-16 h-16 object-cover rounded"
                                    />
                                  )}
                                  <div className="flex-1">
                                    <h5 className="font-medium text-[#3C3C3C] dark:text-gray-100">
                                      {project.name}
                                    </h5>
                                    {project.description && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
                                        {project.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* RIGHT COLUMN - Selected Projects with Headlines */}
                      <div>
                        <h4 className="font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">
                          Selected Projects - Add Headlines
                        </h4>
                        {!newBlockContent.selectedProjects || newBlockContent.selectedProjects.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            Select projects from the left to add headlines
                          </div>
                        ) : (
                          <div className="space-y-4 max-h-[500px] overflow-y-auto">
                            {newBlockContent.selectedProjects.map(project => (
                              <div
                                key={project.projectId}
                                className="border border-gray-200 dark:border-gray-700 rounded p-4 bg-white dark:bg-gray-800"
                              >
                                <div className="flex gap-3 mb-3">
                                  {project.projectPhoto && (
                                    <img
                                      src={project.projectPhoto}
                                      alt={project.projectName}
                                      className="w-20 h-20 object-cover rounded"
                                    />
                                  )}
                                  <div className="flex-1">
                                    <div className="flex items-start justify-between">
                                      <h5 className="font-medium text-[#3C3C3C] dark:text-gray-100">
                                        {project.projectName}
                                      </h5>
                                      <button
                                        type="button"
                                        onClick={() => handleProjectToggle(project.projectId)}
                                        className="text-red-500 hover:text-red-600"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                    {project.projectDescription && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                                        {project.projectDescription}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
                                    Project Headline (optional)
                                  </label>
                                  <textarea
                                    value={project.projectHeadline}
                                    onChange={(e) => handleProjectHeadlineChange(project.projectId, e.target.value)}
                                    placeholder="Add a custom headline for this project..."
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100 resize-none"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white dark:bg-gray-800 p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between">
              <div>
                {newBlockType && (
                  <button
                    onClick={() => setNewBlockType(null)}
                    className="px-4 py-2 text-[#7A99D8] hover:text-[#6A89C8]"
                  >
                    Back
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowAddBlockModal(false);
                    setNewBlockType(null);
                    setNewBlockContent({});
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                {newBlockType && (
                  <button
                    onClick={handleAddBlockToPage}
                    className="px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8] flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add to Page
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#3C3C3C] dark:text-white">
                    Publish Page
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Make your changes live
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  This will publish your draft content and make it visible on your live portal page.
                  Current visitors will see the new content immediately.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPublishConfirm(false)}
                  disabled={publishing}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePublish}
                  disabled={publishing}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {publishing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Publish Now
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish Success Modal */}
      {showPublishSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#3C3C3C] dark:text-white">
                    Page Published Successfully!
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Your changes are now live
                  </p>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800 dark:text-green-200">
                  Your page has been published and is now visible to all visitors on your public portal.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPublishSuccess(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Continue Editing
                </button>
                <button
                  onClick={() => window.open(`/portal/${pageData.user_id}/${pageData.slug}`, '_blank')}
                  className="flex-1 px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8] flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  View Live Page
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageBuilder;
